export interface SoloPrices {
  monthlyCents: number;
  monthlyTrialDays: number;
  yearlyCents: number;
  yearlyTrialDays: number;
}

// Built-in old price, shown whenever the price API cannot be read.
export const FALLBACK_SOLO_PRICES: SoloPrices = {
  monthlyCents: 2900,
  monthlyTrialDays: 7,
  yearlyCents: 29000,
  yearlyTrialDays: 30,
};

const isPositiveInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v > 0;

const isNonNegativeInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v >= 0;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

// All-or-nothing: returns a complete price set or null (caller shows fallback).
export const parseSoloPrices = (body: unknown): SoloPrices | null => {
  if (!isRecord(body) || body.currency !== 'usd') return null;
  const tiers = body.tiers;
  if (!isRecord(tiers) || !isRecord(tiers.Solo)) return null;
  const { monthly, yearly } = tiers.Solo;
  if (!isRecord(monthly) || !isRecord(yearly)) return null;
  if (
    !isPositiveInt(monthly.amount_cents) ||
    !isPositiveInt(yearly.amount_cents) ||
    !isNonNegativeInt(monthly.trial_days) ||
    !isNonNegativeInt(yearly.trial_days)
  ) {
    return null;
  }
  return {
    monthlyCents: monthly.amount_cents,
    monthlyTrialDays: monthly.trial_days,
    yearlyCents: yearly.amount_cents,
    yearlyTrialDays: yearly.trial_days,
  };
};

// Whole dollars without decimals, otherwise two decimals.
export const formatCents = (cents: number): string =>
  cents % 100 === 0
    ? `$${cents / 100}`
    : `$${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;

export const trialLabel = (days: number): string => `${days}-day free trial`;

// Percent saved by paying yearly, from the raw API cents. May be <= 0.
export const savePercent = (p: SoloPrices): number =>
  Math.round(100 - (100 * p.yearlyCents) / (12 * p.monthlyCents));

export interface SoloDisplay {
  monthlyCents: number;
  yearlyPerMonthCents: number;
  annualCents: number;
}

// discountFactor (for example 0.9) is applied in integer cents, with a single
// rounding per figure. It must be a whole percent: it is rounded to one.
export const soloDisplay = (p: SoloPrices, discountFactor = 1): SoloDisplay => {
  const pct = Math.round(discountFactor * 100);
  return {
    monthlyCents: Math.round((p.monthlyCents * pct) / 100),
    yearlyPerMonthCents: Math.round((p.yearlyCents * pct) / 1200),
    annualCents: Math.round((p.yearlyCents * pct) / 100),
  };
};
